import { Menu, RowInput } from '@affine/component';
import type {
  WorkspaceAreaKind,
  WorkspaceAreaRef,
} from '@affine/core/modules/folder-api';
import {
  ArrowDownSmallIcon,
  CheckBoxCheckLinearIcon,
} from '@blocksuite/icons/rc';
import { type ReactElement, useMemo, useState } from 'react';

import * as styles from './app-resource-area-ui.css';
import type {
  CalendarMailboxAreaTransitionAction,
  CalendarMailboxAreaTransitionPreview,
} from './calendar';

type I18nMap = Record<string, unknown>;

export interface AppResourceAreaRef {
  kind: WorkspaceAreaKind;
  name: string;
  ownerUserId: string | null;
  rootFolderId: string;
  systemFolderId: string | null;
  workspaceId: string;
}

export type AppResourceScope = 'private' | 'shared';

export function appResourceAreaScope(
  area: Pick<AppResourceAreaRef, 'kind'>
): AppResourceScope {
  return area.kind === 'private' ? 'private' : 'shared';
}

export function appResourceAreaLabel(
  area: Pick<AppResourceAreaRef, 'kind' | 'name'>,
  t: I18nMap
): string {
  if (area.kind === 'public') {
    return areaText(t, 'com.streamlain.appResourceArea.public', 'Public area');
  }
  if (area.kind === 'private') {
    return area.name.trim()
      ? area.name
      : areaText(t, 'com.streamlain.appResourceArea.private', 'Private area');
  }
  if (area.kind === 'teamspace') {
    return area.name.trim()
      ? area.name
      : areaText(t, 'com.streamlain.appResourceArea.teamspace', 'Teamspace');
  }
  return areaText(t, 'com.streamlain.appResourceArea.app', 'App area');
}

export function defaultAppResourceAreaRootFolderId(
  areas: readonly WorkspaceAreaRef[]
): string {
  return (
    areas.find(
      area => isAppResourceAreaSelectable(area) && area.kind === 'public'
    )?.rootFolderId ??
    areas.find(
      area => isAppResourceAreaSelectable(area) && area.kind === 'private'
    )?.rootFolderId ??
    areas.find(isAppResourceAreaSelectable)?.rootFolderId ??
    ''
  );
}

export function defaultAppResourceAreaRootFolderIdForScope(
  areas: readonly WorkspaceAreaRef[],
  scope: AppResourceScope
): string {
  return (
    areas.find(
      area =>
        isAppResourceAreaSelectable(area) &&
        appResourceAreaScope(area) === scope
    )?.rootFolderId ?? ''
  );
}

export function isAppResourceAreaSelectable(area: WorkspaceAreaRef): boolean {
  return area.kind !== 'app' && area.permissions.Folder_AddDoc === true;
}

export function AppResourceAreaBadge({
  area,
  t,
}: {
  area: Pick<AppResourceAreaRef, 'kind' | 'name'>;
  t: I18nMap;
}): ReactElement {
  return (
    <span className={styles.areaBadge} title={appResourceAreaLabel(area, t)}>
      {appResourceAreaLabel(area, t)}
    </span>
  );
}

export function AppResourceAreaPicker({
  areaFilter,
  areas,
  disabled,
  emptyText,
  getAreaDescription,
  getAreaLabel,
  hint,
  label,
  noSearchResultsText,
  onSelectRootFolderId,
  presentation = 'cards',
  searchPlaceholder,
  selectedRootFolderId,
  t,
}: {
  areaFilter?: (area: WorkspaceAreaRef) => boolean;
  areas: readonly WorkspaceAreaRef[];
  disabled?: boolean;
  emptyText?: string;
  getAreaDescription?: (area: WorkspaceAreaRef) => string;
  getAreaLabel?: (area: WorkspaceAreaRef) => string;
  hint?: string;
  label: string;
  noSearchResultsText?: string;
  onSelectRootFolderId: (rootFolderId: string) => void;
  presentation?: 'cards' | 'dropdown';
  searchPlaceholder?: string;
  selectedRootFolderId: string;
  t: I18nMap;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selectableAreas = useMemo(
    () =>
      areas.filter(
        area =>
          isAppResourceAreaSelectable(area) && (areaFilter?.(area) ?? true)
      ),
    [areaFilter, areas]
  );
  const selectedArea = selectableAreas.find(
    area => area.rootFolderId === selectedRootFolderId
  );
  const filteredAreas = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    if (!normalizedQuery) return selectableAreas;
    return selectableAreas.filter(area => {
      const resolvedLabel =
        getAreaLabel?.(area) ?? appResourceAreaLabel(area, t);
      const resolvedDescription =
        getAreaDescription?.(area) ?? areaDescription(area, t);
      return `${resolvedLabel} ${resolvedDescription}`
        .toLocaleLowerCase()
        .includes(normalizedQuery);
    });
  }, [getAreaDescription, getAreaLabel, query, selectableAreas, t]);

  const selectArea = (rootFolderId: string) => {
    onSelectRootFolderId(rootFolderId);
    setOpen(false);
    setQuery('');
  };

  const dropdown =
    presentation === 'dropdown' && selectableAreas.length > 0 ? (
      <Menu
        rootOptions={{
          open,
          onOpenChange: nextOpen => {
            setOpen(nextOpen);
            if (!nextOpen) setQuery('');
          },
        }}
        contentOptions={{
          align: 'start',
          side: 'bottom',
          sideOffset: 4,
          className: styles.areaDropdownMenu,
        }}
        items={
          <div className={styles.areaDropdownContent}>
            <RowInput
              autoFocus
              className={styles.areaDropdownSearch}
              placeholder={searchPlaceholder}
              value={query}
              onChange={setQuery}
            />
            <div className={styles.areaDropdownList} role="listbox">
              {filteredAreas.length === 0 ? (
                <div className={styles.areaDropdownEmpty}>
                  {noSearchResultsText ?? emptyText}
                </div>
              ) : (
                filteredAreas.map(area => {
                  const selected = area.rootFolderId === selectedRootFolderId;
                  const resolvedLabel =
                    getAreaLabel?.(area) ?? appResourceAreaLabel(area, t);
                  return (
                    <button
                      key={area.rootFolderId}
                      aria-selected={selected}
                      className={styles.areaDropdownOption}
                      role="option"
                      type="button"
                      onClick={() => selectArea(area.rootFolderId)}
                    >
                      <span className={styles.areaOptionText}>
                        <span className={styles.areaOptionTitle}>
                          {resolvedLabel}
                        </span>
                        <span className={styles.areaOptionMeta}>
                          {getAreaDescription?.(area) ??
                            areaDescription(area, t)}
                        </span>
                      </span>
                      {selected ? <CheckBoxCheckLinearIcon /> : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        }
      >
        <button
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label={label}
          className={styles.areaDropdownTrigger}
          disabled={disabled}
          type="button"
        >
          <span>
            {selectedArea
              ? (getAreaLabel?.(selectedArea) ??
                appResourceAreaLabel(selectedArea, t))
              : emptyText}
          </span>
          <ArrowDownSmallIcon />
        </button>
      </Menu>
    ) : null;

  return (
    <div className={styles.areaPicker} data-testid="app-resource-area-picker">
      <p className={styles.areaPickerLabel}>{label}</p>
      {hint ? <p className={styles.areaPickerHint}>{hint}</p> : null}
      {selectableAreas.length === 0 ? (
        <p className={styles.areaPickerHint}>
          {emptyText ??
            areaText(
              t,
              'com.streamlain.appResourceArea.noneAvailable',
              'No available area.'
            )}
        </p>
      ) : presentation === 'dropdown' ? (
        dropdown
      ) : (
        <div className={styles.areaPickerList}>
          {selectableAreas.map(area => {
            const selected = area.rootFolderId === selectedRootFolderId;
            const resolvedLabel =
              getAreaLabel?.(area) ?? appResourceAreaLabel(area, t);
            return (
              <button
                key={area.rootFolderId}
                aria-pressed={selected}
                className={
                  selected ? styles.areaOptionSelected : styles.areaOption
                }
                disabled={disabled}
                type="button"
                onClick={() => onSelectRootFolderId(area.rootFolderId)}
              >
                <span className={styles.areaOptionText}>
                  <span className={styles.areaOptionTitle}>
                    {resolvedLabel}
                  </span>
                  <span className={styles.areaOptionMeta}>
                    {getAreaDescription?.(area) ?? areaDescription(area, t)}
                  </span>
                </span>
                <span className={styles.areaBadge} title={resolvedLabel}>
                  {resolvedLabel}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AppResourceCredentialNotice({
  t,
}: {
  t: I18nMap;
}): ReactElement {
  return (
    <div
      className={styles.credentialNotice}
      data-testid="app-resource-credential-notice"
    >
      <strong>
        {areaText(
          t,
          'com.streamlain.appResourceArea.credentialNoticeTitle',
          'Credentials stay separate'
        )}
      </strong>
      <span>
        {areaText(
          t,
          'com.streamlain.appResourceArea.credentialNoticeBody',
          'Doc permissions decide who can see this resource. Provider credentials are granted separately and can be reviewed before sharing.'
        )}
      </span>
    </div>
  );
}

type AppResourceAreaTransitionPanelProps = {
  pending: boolean;
  preview: CalendarMailboxAreaTransitionPreview;
  t: I18nMap;
} & (
  | {
      presentation?: 'actions';
      onCancel: () => void;
      onMoveTogether: () => void;
      onUnlinkAndMove: () => void;
    }
  | {
      presentation: 'selection';
      selectedAction: CalendarMailboxAreaTransitionAction | null;
      onActionChange: (action: CalendarMailboxAreaTransitionAction) => void;
    }
);

export function AppResourceAreaTransitionPanel(
  props: AppResourceAreaTransitionPanelProps
): ReactElement {
  const { pending, preview, t } = props;
  const calendarTitles = preview.calendarTitles ?? [];
  const mailboxTitles = preview.mailboxTitles ?? [];
  const hasNamedResources =
    preview.componentDetailsVisible &&
    (calendarTitles.length > 0 || mailboxTitles.length > 0);

  return (
    <section
      className={styles.transitionPanel}
      data-testid="app-resource-area-transition"
      aria-label={areaText(
        t,
        'com.streamlain.appResourceArea.transitionTitle',
        'Linked resources'
      )}
    >
      <strong className={styles.transitionTitle}>
        {areaText(
          t,
          'com.streamlain.appResourceArea.transitionTitle',
          'Linked resources'
        )}
      </strong>
      <p className={styles.transitionText}>
        {areaText(
          t,
          'com.streamlain.appResourceArea.transitionBody',
          'This resource is linked to a calendar or mailbox. Keep the linked resources together, or remove the link before moving only this resource.'
        )}
      </p>
      {hasNamedResources ? (
        <div className={styles.transitionResources}>
          {calendarTitles.map((title, index) => (
            <div
              className={styles.transitionResource}
              key={`calendar-${index}-${title}`}
            >
              <span className={styles.transitionResourceKind}>
                {areaText(
                  t,
                  'com.streamlain.appResourceLifecycle.resource.calendar',
                  'Calendar'
                )}
              </span>
              <span className={styles.transitionResourceTitle}>{title}</span>
            </div>
          ))}
          {mailboxTitles.map((title, index) => (
            <div
              className={styles.transitionResource}
              key={`mailbox-${index}-${title}`}
            >
              <span className={styles.transitionResourceKind}>
                {areaText(
                  t,
                  'com.streamlain.appResourceLifecycle.resource.mailbox',
                  'Mailbox'
                )}
              </span>
              <span className={styles.transitionResourceTitle}>{title}</span>
            </div>
          ))}
        </div>
      ) : preview.componentDetailsVisible ? (
        <p className={styles.transitionMeta}>
          {areaText(
            t,
            'com.streamlain.appResourceArea.transitionCount',
            `${preview.calendarCount} calendar(s), ${preview.mailboxCount} mailbox(es)`,
            {
              calendars: String(preview.calendarCount),
              mailboxes: String(preview.mailboxCount),
            }
          )}
        </p>
      ) : null}
      {!preview.canMoveTogether ||
      (preview.activeLinkCount > 0 && !preview.canUnlinkAndMove) ? (
        <p className={styles.transitionMeta} role="status">
          {areaText(
            t,
            'com.streamlain.appResourceArea.transitionPermissionHint',
            'Unavailable actions require additional source, target, calendar, or mailbox permissions.'
          )}
        </p>
      ) : null}
      {props.presentation === 'selection' ? (
        <div
          aria-label={areaText(
            t,
            'com.streamlain.appResourceArea.transitionTitle',
            'Linked resources'
          )}
          className={styles.transitionChoices}
          role="radiogroup"
        >
          <label
            className={`${styles.transitionChoice} ${
              props.selectedAction === 'move_together'
                ? styles.transitionChoiceSelected
                : ''
            }`}
          >
            <input
              checked={props.selectedAction === 'move_together'}
              className={styles.transitionChoiceInput}
              disabled={pending || !preview.canMoveTogether}
              name="app-resource-area-transition"
              onChange={() => props.onActionChange('move_together')}
              type="radio"
              value="move_together"
            />
            <span
              aria-hidden
              className={`${styles.transitionChoiceIndicator} ${
                props.selectedAction === 'move_together'
                  ? styles.transitionChoiceIndicatorSelected
                  : ''
              }`}
            />
            <span className={styles.transitionChoiceLabel}>
              {areaText(
                t,
                'com.streamlain.appResourceArea.moveTogether',
                'Move together'
              )}
            </span>
          </label>
          {preview.activeLinkCount > 0 ? (
            <label
              className={`${styles.transitionChoice} ${
                props.selectedAction === 'unlink_and_move'
                  ? styles.transitionChoiceSelected
                  : ''
              }`}
            >
              <input
                checked={props.selectedAction === 'unlink_and_move'}
                className={styles.transitionChoiceInput}
                disabled={pending || !preview.canUnlinkAndMove}
                name="app-resource-area-transition"
                onChange={() => props.onActionChange('unlink_and_move')}
                type="radio"
                value="unlink_and_move"
              />
              <span
                aria-hidden
                className={`${styles.transitionChoiceIndicator} ${
                  props.selectedAction === 'unlink_and_move'
                    ? styles.transitionChoiceIndicatorSelected
                    : ''
                }`}
              />
              <span className={styles.transitionChoiceLabel}>
                {areaText(
                  t,
                  'com.streamlain.appResourceArea.unlinkAndMove',
                  'Unlink and move only this resource'
                )}
              </span>
            </label>
          ) : null}
        </div>
      ) : (
        <div className={styles.transitionActions}>
          <button
            className={styles.transitionPrimaryAction}
            disabled={pending || !preview.canMoveTogether}
            onClick={props.onMoveTogether}
            type="button"
          >
            {areaText(
              t,
              'com.streamlain.appResourceArea.moveTogether',
              'Move together'
            )}
          </button>
          {preview.activeLinkCount > 0 ? (
            <button
              className={styles.transitionSecondaryAction}
              disabled={pending || !preview.canUnlinkAndMove}
              onClick={props.onUnlinkAndMove}
              type="button"
            >
              {areaText(
                t,
                'com.streamlain.appResourceArea.unlinkAndMove',
                'Unlink and move only this resource'
              )}
            </button>
          ) : null}
          <button
            className={styles.transitionCancelAction}
            disabled={pending}
            onClick={props.onCancel}
            type="button"
          >
            {areaText(
              t,
              'com.streamlain.appResourceArea.cancelTransition',
              'Cancel'
            )}
          </button>
        </div>
      )}
    </section>
  );
}

function areaDescription(area: WorkspaceAreaRef, t: I18nMap): string {
  if (area.kind === 'private') {
    return areaText(
      t,
      'com.streamlain.appResourceArea.privateDescription',
      'Only visible to you.'
    );
  }
  if (area.kind === 'teamspace') {
    return areaText(
      t,
      'com.streamlain.appResourceArea.teamspaceDescription',
      'Visible to people with access to this teamspace.'
    );
  }
  return areaText(
    t,
    'com.streamlain.appResourceArea.publicDescription',
    'Visible according to Public Area permissions.'
  );
}

function areaText(
  t: I18nMap,
  key: string,
  fallback: string,
  params?: Record<string, string>
): string {
  const fn = t[key];
  if (typeof fn !== 'function') return fallback;
  return (fn as (params?: Record<string, string>) => string)(params);
}
